// Screen help: a small "?" in a circle that opens a plain explainer of the whole
// screen — what it is for, what its parts mean, how to use it. Content only, with a
// floating close button (no header bar). Each screen passes its own ScreenHelpContent.
import React, { useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useTheme, makeThemedStyles, DISPLAY_FONT } from './theme';

export interface ScreenHelpSection {
  heading: string;
  /** Plain paragraphs. */
  body?: string[];
  /** Term → meaning rows (e.g. each sector or stage). `color` tints the term. */
  items?: { term: string; text: string; color?: string }[];
}
export interface ScreenHelpContent {
  title: string;
  intro: string;
  sections: ScreenHelpSection[];
}

export function ScreenHelp({ help, style }: { help: ScreenHelpContent; style?: object }) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Pressable onPress={() => setOpen(true)} hitSlop={10} style={[styles.button, style]} accessibilityRole="button" accessibilityLabel={`How ${help.title} works`}>
        <Text style={styles.q}>?</Text>
      </Pressable>
      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <View style={styles.backdrop}>
          <View style={styles.sheet}>
            <ScrollView contentContainerStyle={styles.body}>
              <Text style={styles.title}>{help.title}</Text>
              <Text style={styles.para}>{help.intro}</Text>
              {help.sections.map((s) => (
                <View key={s.heading} style={styles.section}>
                  <Text style={styles.heading}>{s.heading}</Text>
                  {s.body?.map((p, i) => (
                    <Text key={i} style={styles.para}>{p}</Text>
                  ))}
                  {s.items?.map((it) => (
                    <Text key={it.term} style={styles.item}>
                      <Text style={[styles.term, it.color ? { color: it.color } : null]}>{it.term}</Text>
                      {'  '}
                      {it.text}
                    </Text>
                  ))}
                </View>
              ))}
            </ScrollView>
            <Pressable onPress={() => setOpen(false)} hitSlop={10} style={styles.close} accessibilityRole="button" accessibilityLabel="Close">
              <Text style={styles.closeText}>✕</Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    </>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    button: {
      width: 26,
      height: 26,
      borderRadius: 13,
      borderWidth: 1,
      borderColor: t.navyLine,
      alignItems: 'center',
      justifyContent: 'center',
    },
    q: { fontSize: 14, fontWeight: '800', color: t.ivorySoft, lineHeight: 16 },
    backdrop: { flex: 1, backgroundColor: 'rgba(7,11,23,0.72)', alignItems: 'center', justifyContent: 'center', padding: 16 },
    sheet: { width: '100%', maxWidth: 560, maxHeight: '88%', borderRadius: 20, backgroundColor: t.navyRaised, borderWidth: 1, borderColor: t.navyLine, overflow: 'hidden' },
    body: { padding: 20, paddingTop: 22, gap: 12 },
    title: { fontFamily: DISPLAY_FONT, fontSize: 22, color: t.gold, paddingRight: 36 },
    section: { gap: 6, marginTop: 4 },
    heading: { fontFamily: DISPLAY_FONT, fontSize: 14, letterSpacing: 1, color: t.gold, textTransform: 'uppercase' },
    para: { fontSize: 14.5, lineHeight: 21, color: t.ivory },
    item: { fontSize: 14.5, lineHeight: 21, color: t.ivorySoft },
    term: { fontWeight: '800', color: t.ivory },
    close: { position: 'absolute', top: 12, right: 12, width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center', backgroundColor: t.navyLine },
    closeText: { fontSize: 15, color: t.ivory },
  }),
);
